import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Loader2,
  Clock, User, Video, Phone
} from 'lucide-react';
import { api } from '../../lib/api';

const CalendarPage = () => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('week'); // 'day' | 'week'
  const [currentDate, setCurrentDate] = useState(new Date());

  const getWeekDates = useCallback((date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
  }, []);

  const fetchMeetings = useCallback(async () => {
    setLoading(true);
    try {
      let start, end;
      if (viewMode === 'day') {
        start = new Date(currentDate);
        start.setHours(0, 0, 0, 0);
        end = new Date(currentDate);
        end.setHours(23, 59, 59, 999);
      } else {
        const week = getWeekDates(currentDate);
        start = week[0];
        start.setHours(0, 0, 0, 0);
        end = week[6];
        end.setHours(23, 59, 59, 999);
      }

      const res = await api.get(`/admin/meetings/calendar?start=${start.toISOString()}&end=${end.toISOString()}`);
      if (res.ok) {
        const data = await res.json();
        setMeetings(data.meetings || []);
      }
    } catch (err) {
      console.error('Failed to fetch meetings:', err);
    } finally {
      setLoading(false);
    }
  }, [currentDate, viewMode, getWeekDates]);

  useEffect(() => {
    fetchMeetings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMeetings]);

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (viewMode === 'day') d.setDate(d.getDate() + dir);
    else d.setDate(d.getDate() + (dir * 7));
    setCurrentDate(d);
  };

  const goToday = () => setCurrentDate(new Date());

  const formatDate = (d) => d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  const formatTime = (isoStr) => {
    const d = new Date(isoStr);
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const getHeaderLabel = () => {
    if (viewMode === 'day') {
      return currentDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    }
    const week = getWeekDates(currentDate);
    const start = week[0].toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    const end = week[6].toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${start} — ${end}`;
  };

  const getMeetingsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return meetings.filter(m => {
      if (!m.scheduled_at) return false;
      return m.scheduled_at.split('T')[0] === dateStr;
    }).sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
  };

  const hours = Array.from({ length: 12 }, (_, i) => i + 7); // 7h - 18h

  const renderMeetingBlock = (m) => (
    <div
      key={m.id}
      className="bg-tp-primary/10 border-l-3 border-tp-primary rounded-r-lg p-2 mb-1 text-xs hover:bg-tp-primary/20 transition-colors cursor-default"
      style={{ borderLeftWidth: '3px' }}
      data-testid={`meeting-${m.id}`}
    >
      <div className="flex items-center gap-1 font-medium text-tp-primary">
        <Clock className="h-3 w-3" />
        {formatTime(m.scheduled_at)} — {m.duration_minutes || 30}min
      </div>
      <div className="flex items-center gap-1 mt-0.5 text-gray-700">
        <User className="h-3 w-3" />
        {m.candidate_name}
      </div>
      <div className="flex items-center gap-2 mt-1 flex-wrap">
        {m.meeting_type && (
          <Badge variant="outline" className="text-[10px] h-4">{m.meeting_type}</Badge>
        )}
        {m.video_url && (
          <a
            href={m.video_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#4C8F6E] text-white hover:bg-[#3d7a5c] transition-colors"
            data-testid={`join-video-${m.id}`}
          >
            <Video className="h-2.5 w-2.5" />
            Rejoindre
          </a>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-8" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold text-tp-text">Calendrier</h1>
          <p className="text-tp-text-light text-sm">Entretiens planifiés</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-tp-bg rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('day')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${viewMode === 'day' ? 'bg-white shadow text-tp-text font-medium' : 'text-tp-text-light'}`}
              data-testid="view-day"
            >
              Jour
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${viewMode === 'week' ? 'bg-white shadow text-tp-text font-medium' : 'text-tp-text-light'}`}
              data-testid="view-week"
            >
              Semaine
            </button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="nav-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday}>Aujourd'hui</Button>
          <Button variant="outline" size="sm" onClick={() => navigate(1)} data-testid="nav-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <h2 className="text-base md:text-lg font-medium text-tp-text capitalize">{getHeaderLabel()}</h2>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-tp-text-light" />
        </div>
      ) : (
        <Card className="border-tp-border overflow-hidden">
          {viewMode === 'week' ? (
            /* WEEK VIEW */
            <div className="grid grid-cols-7 divide-x divide-gray-100">
              {getWeekDates(currentDate).map((date, idx) => {
                const isToday = date.toDateString() === new Date().toDateString();
                const dayMeetings = getMeetingsForDate(date);
                return (
                  <div key={idx} className={`min-h-[200px] ${isToday ? 'bg-tp-primary/5' : ''}`}>
                    <div className={`p-2 text-center border-b ${isToday ? 'bg-tp-primary text-white' : 'bg-gray-50 text-gray-600'}`}>
                      <p className="text-xs font-medium">{date.toLocaleDateString('fr-FR', { weekday: 'short' })}</p>
                      <p className={`text-lg font-bold ${isToday ? '' : 'text-tp-text'}`}>{date.getDate()}</p>
                    </div>
                    <div className="p-1.5 space-y-1">
                      {dayMeetings.length > 0 ? (
                        dayMeetings.map(renderMeetingBlock)
                      ) : (
                        <p className="text-xs text-gray-300 text-center py-4">—</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* DAY VIEW */
            <div>
              <div className={`p-3 text-center border-b ${currentDate.toDateString() === new Date().toDateString() ? 'bg-tp-primary text-white' : 'bg-gray-50'}`}>
                <p className="font-medium">{currentDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
              </div>
              <div className="divide-y divide-gray-50">
                {hours.map(hour => {
                  const hourMeetings = getMeetingsForDate(currentDate).filter(m => {
                    const h = new Date(m.scheduled_at).getHours();
                    return h === hour;
                  });
                  return (
                    <div key={hour} className="flex min-h-[60px]">
                      <div className="w-16 flex-shrink-0 p-2 text-xs text-gray-400 text-right border-r border-gray-100">
                        {hour}:00
                      </div>
                      <div className="flex-1 p-1.5">
                        {hourMeetings.map(renderMeetingBlock)}
                      </div>
                    </div>
                  );
                })}
              </div>
              {getMeetingsForDate(currentDate).length === 0 && (
                <div className="p-8 text-center">
                  <CalendarIcon className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                  <p className="text-sm text-gray-400">Aucun entretien prévu ce jour</p>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Summary */}
      <div className="mt-4 text-xs text-tp-text-light">
        {meetings.length} entretien{meetings.length !== 1 ? 's' : ''} {viewMode === 'week' ? 'cette semaine' : "aujourd'hui"}
      </div>
    </div>
  );
};

export default CalendarPage;
